import {
    ManageUnitsResource,
    ManageUnitsUnitView,
    ResponsePlanV2,
    UnitFiltersForm,
} from '@mark43/cad-api';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApiClient } from '~/api-client';
import { SocketStatus } from '@m43/sockets-mark43';
import {
    CadNextSocketListener,
    createStreamListener,
    useSocketStatus,
    useStream,
} from '../socket/use-stream';
import { ResponsePlanGeneratedMessage } from '../socket/stream-types';
import { logInfo, logWarn, logErrorMessage } from '../monitoring/log';

/**
 * Canonical header that carries the client-minted action id to the server. The server reads it off
 * the request via TracingData and echoes it back on the `responsePlanGenerated` push so this hook
 * can match the landed plan to the kick that requested it. Mirrors
 * `com.mark43.cad.http.CadHttpHeaders.X_CAD_CLIENT_ACTION_ID`.
 */
const X_CAD_CLIENT_ACTION_ID = 'x-cad-client-action-id';

/**
 * Soft client-side deadline per kick. The worker budget is 30s (server-side); we allow a 10s slack
 * for pub/sub + WebSocket delivery before we surface "not available". The deadline is RECOVERABLE:
 * a late push carrying the still-current uuid is accepted and flips the displayed status back to
 * landed, so we never tear down the listener or forget the uuid when the timer fires.
 *
 * Coupling to watch: the 30s worker budget is the server setting `cad.async_response_plan.timeout_ms`,
 * which a department can raise. The client cannot read that setting. If a department sets it above ~30s,
 * lift this constant to match, or a slow-but-configured department sees a premature (self-healing)
 * "not available" on first load.
 */
const RESPONSE_PLAN_DEADLINE_MS = 40_000;

/** Debounce window for re-kicks driven by rapid filter / nudge churn. */
const REKICK_DEBOUNCE_MS = 1_800;

/**
 * Stable empty array for the no-plan-landed case. MUST be a shared constant, not a fresh `[]` literal
 * per render: `initiallySelectedUnits` is a dependency of the store's plan-merge `useMemo`, which feeds
 * a drawer `useEffect` that setStates. A new `[]` every render makes that memo (and the effect's
 * dependency) change every render, causing a Maximum update depth (infinite re-render) error that
 * broke opening the Manage Units panel on flag-on depts. (`useUnitSelection` stabilizes its derived
 * arrays the same way, via useStableIds.)
 */
const EMPTY_INITIALLY_SELECTED_UNITS: ManageUnitsUnitView[] = [];

export type AsyncResponsePlanStatus = 'idle' | 'pending' | 'landed' | 'failed';

type LandedPayload = {
    initiallySelectedUnits: ManageUnitsUnitView[];
    responsePlanV2?: ResponsePlanV2;
    initialPrimaryUnitId?: number;
};

export type UseAsyncResponsePlanResult = {
    /**
     * Ids the server bucketed RECOMMENDED on the landed plan. Derived from the landed payload's
     * `initiallySelectedUnits` so the store can union them into the visible rows. Empty until a
     * plan lands.
     */
    recommendedUnitIds: Set<number>;
    responsePlanV2?: ResponsePlanV2;
    initialPrimaryUnitId?: number;
    initiallySelectedUnits: ManageUnitsUnitView[];
    status: AsyncResponsePlanStatus;
    /** True until the first plan (or first failure) has landed for this panel session. */
    isFirstLoad: boolean;
    /**
     * True only while a refresh (a kick after the first plan landed) is actively in flight. Goes
     * false the moment the refresh resolves OR gives up (deadline / silent failure), even though
     * `status` stays `'pending'` on the give-up path. The drawer dims the results on this.
     */
    isRefreshing: boolean;
    /**
     * Request a kick. Fires immediately when the socket is CONNECTED; otherwise DEFERS until the
     * CONNECTED transition. The kick is an HTTP POST that reaches the server regardless of socket state,
     * but the plan returns ONLY over the `responsePlanGenerated` stream; while the socket is down we
     * cannot receive that push (our subscription — held open app-level by the keeper — is down with
     * the socket, with no replay/ack), so a POST issued while disconnected loses its answer. The
     * connectivity gate lives here, so every caller (rekick/retry/requestInitialKick) inherits the
     * defer-when-disconnected behavior.
     */
    kick: () => void;
    rekick: () => void;
    retry: () => void;
    /**
     * Alias for `kick`. The store uses this for the FIRST kick (immediate, vs the debounced `rekick`
     * for later filter edits); both defer until CONNECTED via the gate inside `kick`.
     */
    requestInitialKick: () => void;
};

/**
 * Owns the async response-plan lifecycle for one Manage Units panel: POSTs
 * `manage_units/generate_response_plan`, tracks the minted uuid, listens for the matching
 * `responsePlanGenerated` push, exposes the landed plan + coarse status. When `asyncEnabled` is
 * false the hook is fully inert — no kick, no stream listener, `status` `'idle'` — so the flag-off
 * path matches `main`. No PII/CJI is logged: every log payload carries only opaque ids (`eventId`,
 * `clientActionId`) and scalars.
 */
export function useAsyncResponsePlan({
    eventId,
    filtersRef,
    asyncEnabled,
    selectedUnitIdsRef,
}: {
    eventId: number;
    // The store's current `selectedFilters`, as a ref read by the kick at fire-time. A ref (not a
    // value) because the store owns `selectedFilters` BELOW the
    // provider that calls this hook, so the provider never sees filter edits as a value and never
    // re-renders on them. This is the same reason `selectedUnitIdsRef` is a ref. The store writes
    // `.current` every render; the kick reads it post-render.
    filtersRef: { readonly current: UnitFiltersForm };
    asyncEnabled: boolean;
    // Live handle to the store's current effective selection. The kick must carry it (like the sync
    // units query does) so the server's "selected units bypass the filter" exemption fires for the async
    // recommendation too — otherwise removing a unit-type filter silently de-selects an already-queued
    // unit of that type. It is a ref (not a value) because the store computes selection AFTER calling
    // this hook (selection depends on this hook's recommendedUnitIds), so the value isn't available at
    // call time; the kick reads it at fire-time (post-render), by which point the store has populated
    // it. Optional so non-store callers (unit tests) default to an empty selection.
    selectedUnitIdsRef?: { readonly current: number[] };
}): UseAsyncResponsePlanResult {
    const apiClient = useApiClient();
    // Hold the api client in a ref so kick() does not depend on its identity. Re-creating kick (and
    // the debounced rekick + the unmount cleanup that cancels the in-flight deadline timer) whenever
    // the client object identity changes would tear down a pending deadline mid-flight. The ref keeps
    // the timer machinery stable across renders while always invoking the latest client.
    const apiClientRef = useRef(apiClient);
    apiClientRef.current = apiClient;

    const [status, setStatus] = useState<AsyncResponsePlanStatus>('idle');
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    // True only while a *refresh* (a kick after the first plan has landed) is actively in flight.
    // Distinct from `status === 'pending'`: after a refresh gives up (deadline early-return, or a
    // silent refresh-failure push) `status` stays `'pending'` so a late same-uuid push can still
    // recover — but the refresh is no longer in flight. The drawer dims the results on THIS, so a
    // timed-out refresh does not leave the list dimmed indefinitely.
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [landed, setLanded] = useState<LandedPayload | undefined>(undefined);

    // Mirror isFirstLoad into a ref so the deadline timer (captured at kick time) reads the live value
    // without being re-created — the deadline behaves differently on first load vs a refresh.
    const isFirstLoadRef = useRef(isFirstLoad);
    isFirstLoadRef.current = isFirstLoad;

    // The uuid of the in-flight kick. Reading it from a ref (not state) keeps the stream listener and
    // the deadline timer reference-stable while still seeing the latest value — a late same-uuid push
    // must be matched even after re-renders, and only the latest uuid ever wins (stale pushes drop).
    const currentUuidRef = useRef<string | undefined>(undefined);
    // The filters the in-flight kick was fired with (as a JSON string, for cheap equality). A push
    // can land after the dispatcher changes filters but before the debounced rekick fires — the push
    // still carries the old kick's uuid, so the uuid check alone would accept it. Comparing against
    // this ref lets the push handler drop that stale-filter push instead.
    const firedFiltersRef = useRef<string | undefined>(undefined);
    const deadlineTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    // Latest eventId for kick() — kept in a ref so the debounced re-kick and the reconnect handler
    // always send the current value without re-creating the debounced fn on every change. (Filters
    // arrive as `filtersRef`, owned/written by the store below the provider — see the param doc.)
    const eventIdRef = useRef(eventId);
    eventIdRef.current = eventId;

    // --- Connectivity-gate backing refs (see kick() / the connectivity gate below) ----------------
    // True while a kick has been requested but DEFERRED because the socket was not yet CONNECTED. The
    // CONNECTED transition consumes it to fire the deferred kick. Declared here, ahead of kick(), so
    // the gate lives entirely inside kick.
    const pendingInitialKickRef = useRef(false);
    // getSocketStatus is re-bound every render and is only available after useSocketStatus below; hold
    // it in a ref so kick() can read the live socket status without a render-order dependency and
    // without re-creating kick when the binding changes. Reassigned right after useSocketStatus. The
    // initial `() => undefined` placeholder is never actually read — kick() only runs from effects /
    // event handlers (post-render), by which point the real binding is installed.
    const getSocketStatusRef = useRef<() => SocketStatus | undefined>(() => undefined);

    const clearDeadline = useCallback(function clearDeadlineTimer() {
        if (deadlineTimerRef.current !== undefined) {
            clearTimeout(deadlineTimerRef.current);
            deadlineTimerRef.current = undefined;
        }
        // Every kick RESOLUTION path (landed, failed push, enqueue-POST failure, unmount) funnels
        // through here, so clearing the refresh signal centrally guarantees it can never outlive the
        // attempt. The two deadline-timer EXPIRY callbacks are the only exits that do not call this —
        // they clear the flag themselves. A new kick re-arms it right after calling clearDeadline.
        setIsRefreshing(false);
    }, []);

    // The actual fire: mint a fresh uuid, arm the soft deadline, and POST. Separated from the
    // connectivity gate in kick() so the CONNECTED transition can fire directly off the (authoritative)
    // socket event without re-reading getSocketStatus. Always called once we've established the socket
    // is connected - never directly by external callers.
    const fireKick = useCallback(
        function fireResponsePlanKick() {
            pendingInitialKickRef.current = false;
            currentUuidRef.current = crypto.randomUUID();
            const clientActionId = currentUuidRef.current;
            // Stamp the filters this kick fires with, so a push that lands after the dispatcher
            // changes filters (before the debounced rekick fires) can be told apart from a push for
            // the filters the screen currently shows.
            firedFiltersRef.current = JSON.stringify(filtersRef.current);
            setStatus('pending');

            clearDeadline();
            // A kick after the first plan landed is a refresh — dim the results while it is in flight.
            setIsRefreshing(!isFirstLoadRef.current);
            deadlineTimerRef.current = setTimeout(function onDeadlineExpiry() {
                // Soft, recoverable deadline: act only if this uuid is still in-flight and still
                // waiting. First load surfaces a failure; a refresh keeps the last good plan. Never
                // clears currentUuidRef or unsubscribes — a late push with this uuid still recovers
                // to landed.
                if (currentUuidRef.current !== clientActionId) {
                    return;
                }
                // Deadline tripped for the in-flight kick: 40s elapsed with no push.
                // Logged BEFORE the isFirstLoad guard so refresh-trips (silent to the
                // dispatcher, who keeps the prior plan) are still captured. clientActionId joins the
                // server hop logs.
                logWarn('mu_async_deadline', {
                    eventId: eventIdRef.current,
                    clientActionId,
                    isFirstLoad: isFirstLoadRef.current,
                });
                // The refresh (or first load) is over: the deadline tripped. Stop dimming even though
                // `status` stays `'pending'` on the refresh branch (kept so a late push can recover).
                setIsRefreshing(false);
                if (!isFirstLoadRef.current) {
                    return;
                }
                setStatus((prev) => (prev === 'pending' ? 'failed' : prev));
            }, RESPONSE_PLAN_DEADLINE_MS);

            apiClientRef.current
                .request<ManageUnitsResource.GenerateResponsePlan>({
                    method: 'POST',
                    url: 'manage_units/generate_response_plan',
                    headers: { [X_CAD_CLIENT_ACTION_ID]: clientActionId },
                    data: {
                        ...filtersRef.current,
                        eventId: eventIdRef.current,
                        // Carry the current effective selection so the server keeps already-queued units in
                        // the recommendation regardless of filter (parity with the sync units query).
                        selectedUnitIds: selectedUnitIdsRef?.current ?? [],
                    },
                })
                .catch((error: unknown) => {
                    // The kick POST returns immediately; a failure here means the request to enqueue the
                    // job did not land. Mark failed only if this is still the in-flight uuid. The plan
                    // itself arrives async over the stream, so a late push can still recover.
                    // Observability: log the HTTP status only -- NEVER the error/response body
                    // (it can carry unit data = PII). clientActionId joins the server hop logs.
                    const httpStatus = (error as { response?: { status?: number } })?.response?.status;
                    logErrorMessage('mu_async_kick_error', {
                        eventId: eventIdRef.current,
                        clientActionId,
                        httpStatus,
                    });
                    if (currentUuidRef.current === clientActionId) {
                        // Resolve this attempt now: the kick POST itself failed, so cancel the deadline
                        // timer — otherwise it fires 40s later and logs a spurious `mu_async_deadline`
                        // for what was actually an enqueue failure, polluting the delivery-gap metric.
                        // A late push (if the job did run) still recovers via the stream listener.
                        clearDeadline();
                        setStatus((prev) => (prev === 'pending' ? 'failed' : prev));
                    }
                });
        },
        // selectedUnitIdsRef and filtersRef are the store's stable useRef objects (identity never
        // changes), so this never re-creates fireKick; they're listed to satisfy exhaustive-deps
        // honestly rather than suppressing the rule.
        [clearDeadline, selectedUnitIdsRef, filtersRef]
    );

    // Connectivity gate. The plan returns ONLY over the `responsePlanGenerated` stream. That
    // subscription is held open app-level by the keeper (ResponsePlanGeneratedSubscriptionKeeper) for
    // the whole session, but — like every subscription — it only receives while the socket is
    // CONNECTED (no replay, no ack). So a kick issued while the socket is down still POSTs to the server,
    // which answers in ~1s and broadcasts to a transport we cannot receive on, dropping the push (the
    // dev43 "Recommendations not available" on cold open, socket-down variant). The gate lives HERE —
    // inside kick() — so EVERY entry point (the initial kick, the debounced rekick, and the user
    // Retry) defers while disconnected, not just the first kick. When deferred, the CONNECTED
    // transition (socketStatusListener below) fires the kick.
    const kick = useCallback(
        function kickResponsePlan() {
            if (!asyncEnabled) {
                return;
            }
            const socketStatus = getSocketStatusRef.current();
            if (socketStatus === 'CONNECTED') {
                fireKick();
                return;
            }
            // Not connected: defer. Mark the pending-initial-kick flag so the CONNECTED transition
            // fires it, show the gathering state so the panel isn't blank, and arm an offline safety
            // net so a socket that never connects surfaces the failure UI ("Recommendations not
            // available" + Retry) instead of "Gathering" forever. The deferred kick clears this timer
            // (via fireKick -> clearDeadline) when it fires on connect.
            pendingInitialKickRef.current = true;
            setStatus('pending');
            clearDeadline();
            // A deferred refresh is still in flight (queued until reconnect) — dim; bounded by the
            // offline deadline below so it cannot dim forever if the socket never returns.
            setIsRefreshing(!isFirstLoadRef.current);
            deadlineTimerRef.current = setTimeout(function onDeferredKickDeadline() {
                setIsRefreshing(false);
                if (pendingInitialKickRef.current && isFirstLoadRef.current) {
                    setStatus((prev) => (prev === 'pending' ? 'failed' : prev));
                }
            }, RESPONSE_PLAN_DEADLINE_MS);
        },
        [asyncEnabled, fireKick, clearDeadline]
    );

    const rekick = useMemo(
        () => debounce(kick, REKICK_DEBOUNCE_MS, { trailing: true }),
        [kick]
    );

    useEffect(
        function cancelPendingWorkOnUnmount() {
            return () => {
                rekick.cancel();
                clearDeadline();
            };
        },
        [rekick, clearDeadline]
    );

    const retry = useCallback(
        function retryResponsePlan() {
            // Cancel any pending debounced rekick first, so Retry and an in-flight debounce cannot
            // fire two concurrent POSTs.
            rekick.cancel();
            // Dispatcher clicked Retry. Logs the failed attempt's uuid being retried;
            // kick() then mints a fresh uuid.
            logInfo('mu_async_retry', {
                eventId: eventIdRef.current,
                clientActionId: currentUuidRef.current,
            });
            kick();
        },
        [kick, rekick]
    );

    // --- Connection lifecycle: fire deferred kicks on connect + reconnect re-kicks ----------------
    // This listener owns both the deferred-kick-on-connect and the reconnect re-kick.
    const socketStatusListener = useMemo<CadNextSocketListener>(
        // NOTE: deliberately NOT invokeOnInitialize. We only want to react to real status TRANSITIONS
        // after mount (the deferred-kick on connect, and reconnect re-kicks). The "are we connected
        // right now?" question is answered synchronously by getSocketStatus() inside kick(). If this
        // replayed the current status on mount, a long-lived socket that had reconnected earlier
        // (timesConnected > 1) would trip the reconnect branch immediately and fire a spurious kick
        // with the not-yet-resolved initial filters.
        () => ({
            onSocketStatusChange: (event) => {
                if (!asyncEnabled || event.status !== 'CONNECTED') {
                    return;
                }
                if (pendingInitialKickRef.current) {
                    // Cold/deferred open: the socket just reached CONNECTED, so the subscription has now
                    // been (re)sent — fire the deferred kick directly off this authoritative CONNECTED
                    // event (fireKick, not kick) so we don't depend on whether getSocketStatus() has
                    // flipped yet. The early return guarantees a reconnect that resolves a deferred load
                    // fires EXACTLY one kick (no extra reconnect re-kick). fireKick clears the pending
                    // flag + the offline deadline timer.
                    fireKick();
                    return;
                }
                if (event.timesConnected > 1) {
                    // A reconnect (not the first connect) may have dropped a push while we were down —
                    // re-kick with a fresh uuid (debounced to coalesce reconnect storms; gated by kick()
                    // but we are CONNECTED here so it fires).
                    // Observability: logs the current (about-to-be-superseded)
                    // uuid -- the fresh uuid is minted later inside the debounced kick.
                    logInfo('mu_async_reconnect_rekick', {
                        eventId: eventIdRef.current,
                        clientActionId: currentUuidRef.current,
                    });
                    rekick();
                }
            },
        }),
        [asyncEnabled, fireKick, rekick]
    );
    const { getSocketStatus } = useSocketStatus(socketStatusListener);
    getSocketStatusRef.current = getSocketStatus;

    const streamListener = useMemo(() => {
        if (!asyncEnabled) {
            return undefined;
        }
        return createStreamListener({
            streamNames: ['responsePlanGenerated'] as const,
            onMessage: (message) => {
                const push: ResponsePlanGeneratedMessage = message.payload;
                // Accept only the push for the current in-flight uuid AND the current event. Pushes for
                // a superseded kick (stale uuid) or a different event are dropped — latest-uuid-wins.
                // Also reject any push that arrives before the first kick mints a uuid: an unkeyed push
                // (clientActionId undefined) would otherwise match the also-undefined currentUuidRef and
                // land a plan that was never requested. Also reject a push whose uuid still matches but
                // whose filters no longer match the screen: the dispatcher changed filters after this
                // kick fired (a rekick is already pending, debounced), so this push was computed for
                // filters the screen no longer shows. Drop it and let the pending rekick refresh.
                if (
                    !currentUuidRef.current ||
                    push.clientActionId !== currentUuidRef.current ||
                    push.eventId !== eventIdRef.current ||
                    JSON.stringify(filtersRef.current) !== firedFiltersRef.current
                ) {
                    return;
                }
                if (push.failed) {
                    // Failure on first load: flip to `failed` but keep `isFirstLoad` true so the
                    // drawer's `failed && isFirstLoad` gate renders. Failure on a refresh is silent —
                    // keep the prior landed payload. Either way the deadline timer is cleared.
                    clearDeadline();
                    if (isFirstLoadRef.current) {
                        setStatus('failed');
                    }
                    return;
                }
                clearDeadline();
                setLanded({
                    initiallySelectedUnits: push.initiallySelectedUnits ?? [],
                    responsePlanV2: push.responsePlanV2,
                    initialPrimaryUnitId: push.initialPrimaryUnitId,
                });
                setStatus('landed');
                setIsFirstLoad(false);
                // Round-trip landed: the anchor "round trips completed" signal, keyed by
                // clientActionId so it joins the server hop logs.
                logInfo('mu_async_landed', {
                    eventId: push.eventId,
                    clientActionId: push.clientActionId,
                });
            },
        });
        // `filtersRef` is the store's stable useRef (identity never changes — see the param doc), so
        // listing it here satisfies exhaustive-deps without ever re-subscribing the stream.
    }, [asyncEnabled, clearDeadline, filtersRef]);

    // When `asyncEnabled` is false, `streamListener` is undefined and `useStream` subscribes nothing.
    // Reconnect and initial-connect gating are owned by `socketStatusListener` above, not the stream
    // listener.
    useStream(streamListener);

    // recommendedUnitIds is derived from the rows the server bucketed RECOMMENDED on the landed
    // payload — NOT from the raw responsePlanV2.unitIdsAlreadyInResponsePlan. The raw plan id set
    // includes units that are unavailable, assigned elsewhere, or filtered off-screen; selecting
    // those is a legacy nudge-only behavior that RND-62325 removed. The store unions this set with
    // its visible rows, so a recommended id with no visible/selectable row is a no-op there.
    const recommendedUnitIds = useMemo(() => {
        const initiallySelected = landed?.initiallySelectedUnits ?? [];
        return new Set(
            initiallySelected
                .filter((unit) => unit.manageUnitGroup === 'RECOMMENDED')
                .map((unit) => unit.id)
        );
    }, [landed]);

    return {
        recommendedUnitIds,
        responsePlanV2: landed?.responsePlanV2,
        initialPrimaryUnitId: landed?.initialPrimaryUnitId,
        initiallySelectedUnits: landed?.initiallySelectedUnits ?? EMPTY_INITIALLY_SELECTED_UNITS,
        status,
        isFirstLoad,
        isRefreshing,
        kick,
        rekick,
        retry,
        // requestInitialKick is now exactly the gated kick (the connectivity gate moved into kick), so
        // the store's "first kick" entry point defers-when-disconnected just like rekick/retry. Kept as
        // a distinct name so the store call site still reads as "the initial kick".
        requestInitialKick: kick,
    };
}
